/**
 * Mock: Chat Stream with Async Task Polling (Flight Search)
 *
 * Flow:
 *   1. User sends "Show me flights from Delhi to Mumbai on 10 Mar 2026"
 *   2. Chat stream emits THINKING events → then FINAL_RESPONSE with asyncTask → then STREAM_END
 *   3. Frontend starts polling the pollEndpoint using pollMethod
 *   4. Poll returns PROCESSING (1-N times) → then COMPLETED (with full AssistantMessage, appended as new message)
 *
 * This file contains mock responses for each stage of the flow.
 */

// ============================================================================
// 1. Chat Stream Events (SSE)
// ============================================================================

/**
 * THINKING events — emitted while backend is processing
 */
export const MOCK_THINKING_EVENTS = [
  {
    eventType: 'THINKING' as const,
    crId: 'session_abc123',
    thinkingMessage: '📍 Searching flights from Delhi to Mumbai...',
    timestamp: 1741564800000,
  },
  {
    eventType: 'THINKING' as const,
    crId: 'session_abc123',
    thinkingMessage: '✈️ Checking 6 airlines for best fares...',
    timestamp: 1741564802000,
  },
];

/**
 * FINAL_RESPONSE event — contains asyncTask to trigger polling.
 * The `content` has a placeholder message shown while polling runs.
 * The `asyncTask` tells the frontend how and where to poll.
 */
export const MOCK_FINAL_RESPONSE_WITH_ASYNC_TASK = {
  eventType: 'FINAL_RESPONSE' as const,
  crId: 'session_abc123',
  response: {
    id: 'msg_async_001',
    role: 'assistant' as const,
    content: [
      {
        type: 'text' as const,
        value: 'Searching for the best flights... This may take a moment.',
      },
      {
        type: 'action-button' as const,
        label: 'Click here to check availability later',
        action: {
          type: 'api-call' as const,
          endpoint: '/api/flights/search/poll',
          method: 'GET' as const,
          params: {
            crId: 'FLT-MCP::session_abc123::UPHVGED7UM::DEL-BOM-20260310::1-0-0::E',
            sessionId: 'session_abc123',
            requestId: '8ab1db0c-04fe-4b25-a15f-81a74abbf838',
          },
        },
      },
    ],
    timestamp: '2026-03-10T10:00:05.000Z',
    asyncTask: {
      taskId: 'flt_search_task_xyz789',
      taskType: 'FLIGHT_SEARCH' as const,
      pollEndpoint: '/api/flights/search/poll',
      pollMethod: 'GET' as const,
      params: {
        crId: 'FLT-MCP::session_abc123::UPHVGED7UM::DEL-BOM-20260310::1-0-0::E',
        sessionId: 'session_abc123',
        requestId: '8ab1db0c-04fe-4b25-a15f-81a74abbf838',
      },
      pollIntervalMs: 2000,
      maxAttempts: 30,
    },
  },
  timestamp: 1741564805000,
};

/**
 * STREAM_END event — stream closes after FINAL_RESPONSE.
 * Frontend does NOT call completeStreaming() here because asyncTask is active.
 */
export const MOCK_STREAM_END = {
  eventType: 'STREAM_END' as const,
  crId: 'session_abc123',
  timestamp: 1741564806000,
};

// ============================================================================
// 2. Poll API Responses (GET /api/flights/search/poll?taskId=flt_search_task_xyz789)
// ============================================================================

/**
 * Poll response: PROCESSING — search is still running.
 * Frontend shows the `message` as a thinking indicator.
 */
export const MOCK_POLL_PROCESSING = {
  status: 'PROCESSING' as const,
  progress: 40,
  message: '✈️ Found 42 flights, comparing fares...',
};

/**
 * Poll response: COMPLETED — search finished successfully.
 * The `response` field contains the full AssistantMessage (appended as a new message, NOT replacing the initial one).
 * Frontend dispatches appendPollResultMessage + updateTemplateFromStream + completeStreaming.
 */
export const MOCK_POLL_COMPLETED = {
  status: 'COMPLETED' as const,
  response: {
    id: 'msg_async_002',
    role: 'assistant' as const,
    content: [
      {
        type: 'markdown' as const,
        value:
          '**Flights from Delhi to Mumbai on 10 Mar 2026**\n\n- **Total options:** 96 flights (5 airlines, 66 nonstop)\n- **Price range:** **₹4,626 – ₹11,149**\n- **Best picks:**\n  - **Budget:** ₹4,626 – 2h 5m, dep 11:15 → arr 13:20 (Air India Express, IX-2152)\n  - **Fast:** ₹5,539 – 2h 0m, dep 15:00 → arr 17:00 (Air India Express, IX-1242)\n\nWhich of these options fits your schedule best?',
      },
      {
        type: 'text' as const,
        value: 'Here are the flight options for your search',
      },
      {
        type: 'card' as const,
        thumbnail: {
          imageUrl: 'https://promos.makemytrip.com/images/mmtai/ic-flight-listng-thumbnail.webp',
          label: 'See flight listing',
        },
        templateType: 'flights-listing',
        templateData: {
          presentationMode: 'multiple-options',
          header: {
            optionsCount: 8,
            label: '8 Flight Options',
          },
          route: {
            mode: 'one-way',
            origin: { code: 'DEL', city: 'New Delhi', country: 'India' },
            destination: { code: 'BOM', city: 'Mumbai', country: 'India' },
          },
          flights: [],
        },
      },
    ],
    activeTemplate: 'flights-listing' as const,
    templateData: {
      presentationMode: 'multiple-options',
      header: {
        optionsCount: 8,
        label: '8 Flight Options',
      },
      route: {
        mode: 'one-way',
        origin: { code: 'DEL', city: 'New Delhi', country: 'India' },
        destination: { code: 'BOM', city: 'Mumbai', country: 'India' },
      },
      flights: [],
    },
    timestamp: '2026-03-10T10:00:15.000Z',
  },
};

/**
 * Poll response: FAILED — search could not complete.
 * The `response` field contains an AssistantMessage with an error message for the user.
 * Frontend dispatches appendPollResultMessage + completeStreaming (no template update).
 */
export const MOCK_POLL_FAILED = {
  status: 'FAILED' as const,
  error: {
    code: 'SEARCH_TIMEOUT',
    message: 'Flight search timed out after 60 seconds',
  },
  response: {
    id: 'msg_async_fail_001',
    role: 'assistant' as const,
    content: [
      {
        type: 'text' as const,
        value:
          'Sorry, the flight search took too long. This can happen during peak hours. Could you try again?',
      },
      {
        type: 'leading-question' as const,
        questions: [
          {
            question: 'Try searching again',
            answer: 'Show me flights from Delhi to Mumbai on 10 Mar 2026',
          },
        ],
      },
    ],
    timestamp: '2026-03-10T10:01:05.000Z',
  },
};
